import {useState, useEffect, useRef} from 'react';
import {Outlet, useLocation, Link, useNavigate} from 'react-router-dom';
import {useTheme} from '../../hooks/useTheme';
import {useResponsiveLayout} from '../../hooks/useResponsiveLayout';
import {useAuth} from '../../contexts/AuthContext';
import api from '../../api/axios';
import { usePopupFlow } from '../../hooks/usePopupFlow';

// Professional icon components
function IconGoogle({className = ''}) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path fill="#4285F4"
                  d="M12 11.5v2.8h3.9c-.2 1.2-1.2 3.4-3.9 3.4-2.4 0-4.4-2-4.4-4.4s2-4.4 4.4-4.4c1.4 0 2.3.6 2.8 1.1l1.9-1.8C16.5 6.1 14.6 5 12 5 7.6 5 4 8.6 4 13s3.6 8 8 8c4.6 0 7.7-3.2 7.7-7.7 0-.6-.1-1.1-.2-1.6H12z"/>
            <path fill="#34A853"
                  d="M4 13c0 1 .3 1.9.8 2.7L6.9 14.6C6.6 14 6.5 13.5 6.5 13s.1-1 .4-1.6L4.8 9.9C4.3 10.7 4 11.8 4 13z"/>
            <path fill="#FBBC05"
                  d="M12 5c1.6 0 3 .6 4 1.6l-2 1.9C13.8 8 12.9 7.6 12 7.6 10 7.6 8.3 9 7.7 11.1L4.8 9.9C5.6 7 8.4 5 12 5z"/>
            <path fill="#EA4335" d="M19.7 7.9L17.8 9.8C17.2 9.1 15.9 7.6 12 7.6v-2.6c2.6 0 4.5 1.1 5.7 2.9z"/>
        </svg>
    );
}

function IconApple({className = ''}) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path fill="currentColor"
                  d="M16.365 1.43c-.9.1-1.98.6-2.6 1.4-.56.7-1.06 1.8-.8 2.8 1.1.1 2.3-.6 3.1-1.4.6-.6 1-1.6.3-3zM12.5 5.3c-1.6 0-3.3.9-4.2.9-.9 0-2.3-1-3.8-1C2.4 5.2 1 7.1 1 10.5c0 1.3.2 2.6.6 3.7 1 2.7 3.4 6.8 6.7 6.7 1.3 0 1.8-.8 3.4-.8 1.6 0 2 .8 3.4.8 3.3.1 5.6-4 6.7-6.7.4-1.2.6-2.5.6-3.7C23 7.1 21.6 5.2 18.8 5.2c-1.5 0-2.9 1-3.8 1-1 0-2.6-.9-4.2-.9z"/>
        </svg>
    );
}

function IconGithub({className = ''}) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path fill="currentColor"
                  d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.38 7.86 10.9.58.11.79-.25.79-.56 0-.28-.01-1.02-.02-2-3.2.7-3.88-1.4-3.88-1.4-.53-1.35-1.3-1.71-1.3-1.71-1.06-.72.08-.71.08-.71 1.17.08 1.78 1.2 1.78 1.2 1.04 1.78 2.72 1.27 3.38.97.11-.76.41-1.27.74-1.56-2.55-.29-5.24-1.27-5.24-5.66 0-1.25.45-2.27 1.19-3.07-.12-.29-.52-1.46.11-3.04 0 0 .97-.31 3.18 1.18a11.07 11.07 0 0 1 2.9-.39c.98 0 1.97.13 2.9.39 2.2-1.49 3.17-1.18 3.17-1.18.63 1.58.23 2.75.11 3.04.74.8 1.19 1.82 1.19 3.07 0 4.4-2.69 5.36-5.25 5.65.42.36.8 1.08.8 2.18 0 1.57-.01 2.83-.01 3.22 0 .31.21.68.8.56C20.71 21.38 24 17.08 24 12c0-6.27-5.23-11.5-12-11.5z"/>
        </svg>
    );
}

function IconMicrosoft({className = ''}) {
    return (
        <svg className={className} viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect x="0" y="0" width="9" height="9" fill="#F25022"/>
            <rect x="12" y="0" width="9" height="9" fill="#7FBA00"/>
            <rect x="0" y="12" width="9" height="9" fill="#00A4EF"/>
            <rect x="12" y="12" width="9" height="9" fill="#FFB900"/>
        </svg>
    );
}

export default function AuthPopupLayout() {
    const {theme, setThemeMode} = useTheme();
    const {containerRef, constraints} = useResponsiveLayout();
    const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
    const [ssoEmail, setSsoEmail] = useState('');
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const [lastUsedProvider, setLastUsedProvider] = useState(() => localStorage.getItem('last_social_provider') || '');
    const scrollContainerRef = useRef(null);
    const location = useLocation();
    const auth = useAuth();
    
    const { isValidating, validationError, isExternal, prompt } = usePopupFlow();

    const isSsoRoute = location.pathname === '/auth/popup/sso';
    const isPasswordRoute = location.pathname === '/auth/popup/password';
    const isRegisterRoute = location.pathname === '/auth/popup/register';
    const isForgotRoute = location.pathname === '/auth/popup/forget';
    const isVerifyRoute = location.pathname === '/auth/popup/verify-interrupted';
    const isNextRoute = location.pathname === '/auth/popup/next';

    const isLoginLikeRoute = isSsoRoute || isPasswordRoute || isRegisterRoute;

    const socialProviders = [
        {
            name: 'Google',
            icon: IconGoogle,
            color: 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-700'
        },
        {
            name: 'Microsoft',
            icon: IconMicrosoft,
            color: 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
        },
        {
            name: 'GitHub',
            icon: IconGithub,
            color: 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
        },
        {
            name: 'Apple',
            icon: IconApple,
            color: 'bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-black/20 dark:border-white/20'
        },
    ];

    const navigate = useNavigate();

    // Direct-access guard: if this layout is rendered in a top-level tab (no opener),
    // immediately redirect back to the main site. This prevents users from opening
    // popup routes directly in a browser tab.
    useEffect(() => {
        if (typeof window !== 'undefined' && window.opener === null && !location.pathname.startsWith('/auth/popup/next')) {
            window.location.href = '/';
        }
    }, [location.pathname]);

    // Redirection to select-account for external flow
    useEffect(() => {
        if (!isValidating && !validationError) {
            if (isExternal) {
                const accountsList = auth?.getAccountsList ? auth.getAccountsList() : [];
                if (accountsList.length > 0) {
                    if (prompt !== 'login' && prompt !== 'signup' && location.pathname !== '/auth/select-account') {
                        navigate('/auth/select-account' + location.search);
                    }
                }
            }
        }
    }, [isValidating, validationError, isExternal, prompt, auth, navigate, location.pathname, location.search]);

    const themeDropdownRef = useRef(null);

    const handleSocialSignIn = async (provider) => {
        // persist last used provider for UI
        localStorage.setItem('last_social_provider', provider);
        setLastUsedProvider(provider);

        try {
            // Ask the backend to initiate social sign-in. The backend will typically
            // return a redirect URL to the provider's OAuth consent page. This
            // component does not handle final token storage — that is the job of
            // the OAuth callback (`AuthPopupNext`).
            const res = await api.post(`/auth/social/${provider.toLowerCase()}`);
            const data = res?.data || {};

            // If the backend requires an intermediate confirmation or created a
            // temporary session, navigate to the verification/confirmation flow.
            if (data.requiresConfirmation || data.tempSession || data.missingFields) {
                navigate('/auth/popup/verify-interrupted', {
                    state: {
                        socialParams: data,
                        requiresEmail: !!data.email,
                        requiresPhone: !!data.phone,
                    },
                });
                return;
            }

            // If backend provided a redirect URL, follow it to continue the OAuth flow.
            if (data.redirectUrl) {
                window.location.assign(data.redirectUrl);
                return;
            }

            // Fallback log for clarity; actual token handling occurs in the callback route.
            console.log(`Social sign-in initiated for ${provider} — awaiting backend flow`, data);
        } catch (err) {
            console.error('Social sign-in error', err);
        }
    };

    const handleSsoSubmit = (e) => {
        e.preventDefault();
        (async () => {
            try {
                // Trigger magic link / SSO login flow. Backend will set refresh cookie as appropriate.
                // Note: we don't pass a password for magic link flows; server should accept email + remember flag.
                await auth.login(ssoEmail, null, false);
                console.log('Magic link requested for', ssoEmail);
            } catch (err) {
                console.error('SSO submit failed', err);
            }
        })();
    };

    // Track scroll position to show indicator on mobile
    useEffect(() => {
        const handleScroll = () => {
            if (scrollContainerRef.current) {
                const {scrollHeight, clientHeight, scrollTop} = scrollContainerRef.current;
                const isScrollable = scrollHeight > clientHeight;
                const isNotAtBottom = scrollTop < scrollHeight - clientHeight - 20;
                setShowScrollIndicator(isScrollable && isNotAtBottom);
            }
        };

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll(); // Initial check
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Close theme/profile dropdown when clicking outside
    useEffect(() => {
        const handleOutside = (e) => {
            if (themeDropdownOpen && themeDropdownRef.current && !themeDropdownRef.current.contains(e.target)) {
                setThemeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [themeDropdownOpen]);

    return (
        <>
            {/* Top Navigation Bar - Absolute Root Level (100% Full-Bleed) */}
            <div
                className="bg-white dark:bg-[#0d0e12] border-b border-slate-200 dark:border-[#1f2937]"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '64px',
                    zIndex: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingLeft: '24px',
                    paddingRight: '24px',
                }}
            >
                <div className="flex items-center gap-3">
                    <div
                        className="h-8 w-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md flex items-center justify-center text-white font-bold text-sm">OL
                    </div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">One Login</div>
                </div>

                {/* Theme Switch in Top Nav */}
                <div>
                    <div className="relative" ref={themeDropdownRef}>
                        <button
                            onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
                            className="p-2 text-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/20 rounded-lg transition-colors text-slate-900 dark:text-white"
                            aria-label="Theme selector"
                        >
                            {theme === 'light' ? '☀️' : theme === 'dark' ? '🌙' : '🖥️'}
                            <span
                                className="sr-only">{theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'}</span>
                        </button>

                        {themeDropdownOpen && (
                            <div
                                className="absolute top-full mt-2 right-0 z-50 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-2xl">
                                <div className="p-2 border-b border-slate-200 dark:border-slate-600">
                                    <p className="text-xs font-semibold text-slate-900 dark:text-white">Theme</p>
                                </div>
                                <div className="p-1 space-y-1">
                                    {[
                                        {value: 'light', icon: '☀️', label: 'Light'},
                                        {value: 'dark', icon: '🌙', label: 'Dark'},
                                        {value: 'system', icon: '🖥️', label: 'System'},
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                setThemeMode(option.value);
                                                setThemeDropdownOpen(false);
                                            }}
                                            className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 text-sm transition-colors ${
                                                theme === option.value
                                                    ? 'bg-indigo-50 dark:bg-indigo-900/80 text-indigo-600 dark:text-indigo-200'
                                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                            }`}
                                        >
                                            <span className="text-base">{option.icon}</span>
                                            <div className="flex-1">{option.label}</div>
                                            {theme === option.value &&
                                                <span className="text-indigo-600 dark:text-indigo-400">✓</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content - Fluid Flexbox Centering */}
            <div ref={scrollContainerRef}
                 className="min-h-screen w-full bg-slate-50 dark:bg-brand-dark flex flex-col items-center justify-center px-4 pt-20 pb-8 overflow-y-auto relative">

                {/* Main Container with Responsive Collision Protection */}
                <div
                    ref={containerRef}
                    className={`w-full max-w-md bg-white dark:bg-brand-card backdrop-blur-sm border border-slate-200 dark:border-gray-800 rounded-2xl shadow-2xl transition-all duration-300`}
                >
                    {/* ============= CARD HEADER ============= */}
                    <div className={`${constraints.containerPadding} border-b border-slate-200 dark:border-gray-800`}>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                                {isNextRoute ? 'Sign-In Callback' : 'One Login SSO'}
                            </h1>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                {isNextRoute ? 'Completing Google authentication' : 'Enterprise identity gateway'}
                            </p>
                        </div>
                    </div>

                    {/* ============= CONTENT AREA ============= */}
                    <div className={`${constraints.containerPadding} space-y-6 text-slate-900 dark:text-white`}>
                        {isValidating ? (
                            <div className="py-8 text-center text-slate-500">
                                <div className="animate-spin inline-block w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mb-2"></div>
                                <p>Validating application...</p>
                            </div>
                        ) : validationError ? (
                            <div className="py-8 text-center">
                                <div className="text-rose-500 mb-2">
                                    <svg className="w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Access Denied</h3>
                                <p className="text-sm text-slate-500 mt-2">{validationError}</p>
                            </div>
                        ) : (
                            <>
                                {/* ============= SOCIAL BUTTONS CONTAINER (Always visible on login-like routes) ============= */}
                        {isLoginLikeRoute && (
                            <div className="w-full grid grid-cols-4 gap-3 sm:grid-cols-4">
                                {socialProviders.map((provider) => {
                                    const IconComponent = provider.icon;
                                    return (
                                        <button
                                            key={provider.name}
                                            type="button"
                                            onClick={() => handleSocialSignIn(provider.name)}
                                            className={`relative flex flex-col items-center justify-center gap-2 py-3 px-2 rounded-lg ${provider.color} transition-all hover:shadow-md`}
                                            aria-label={`Sign in with ${provider.name}`}
                                        >
                                            {provider.name === lastUsedProvider && (
                                                <span
                                                    className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded-full shadow-sm select-none whitespace-nowrap z-10">
                          Last used
                        </span>
                                            )}
                                            <IconComponent className="w-6 h-6 text-slate-800 dark:text-white"/>
                                            <span
                                                className="text-xs font-medium text-center hidden sm:block text-slate-800 dark:text-white">{provider.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* ============= DYNAMIC ROUTE-BASED CONTENT ============= */}

                        {/* SSO FLOW (/auth/popup/sso) */}
                        {isSsoRoute && (
                            <div className="space-y-4">
                                {/* Secondary toggle button: Switch to password login */}
                                <Link
                                    to={`/auth/popup/password${location.search}`}
                                    className="!block !w-full !text-center !text-sm !text-slate-700 dark:!text-slate-200 !bg-slate-100 dark:!bg-slate-800 !border !border-slate-300 dark:!border-slate-700 !rounded-lg !py-2.5 !px-3 hover:!bg-slate-200 dark:hover:!bg-slate-700 !transition-colors !font-medium"
                                >
                                    Continue with password
                                </Link>

                                {/* Divider: must sit immediately below the secondary button and above the email input workspace */}
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"/>
                                    <span
                                        className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">or</span>
                                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"/>
                                </div>

                                {/* Email input and primary button workspace (below divider) */}
                                <form onSubmit={handleSsoSubmit} className="space-y-3">
                                    <div>
                                        <label htmlFor="sso-email"
                                               className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Work Email
                                        </label>
                                        <input
                                            id="sso-email"
                                            type="email"
                                            value={ssoEmail}
                                            onChange={(e) => setSsoEmail(e.target.value)}
                                            placeholder="you@company.com"
                                            className="w-full bg-white dark:bg-gray-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2 text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        />
                                    </div>

                                    {/* Primary button: Send Magic Login Link */}
                                    <button
                                        type="submit"
                                        className="!block !w-full !text-center !text-sm !text-white !bg-[#7c3aed] hover:!bg-[#6d28d9] !border-none !rounded-lg !py-3 !px-4 !font-semibold !cursor-pointer !transition-colors"
                                    >
                                        Send Magic Login Link
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* PASSWORD FLOW (/auth/popup/password) */}
                        {isPasswordRoute && (
                            <div className="space-y-4">
                                {/* Primary link: Continue with SSO (navigates back to SSO root) */}
                                <Link
                                    to={`/auth/popup/sso${location.search}`}
                                    className="!block !w-full !text-center !text-sm !text-white !bg-[#7c3aed] hover:!bg-[#6d28d9] !border-none !rounded-lg !py-2.5 !px-4 !font-semibold !cursor-pointer !transition-colors shadow-sm"
                                >
                                    Continue with SSO
                                </Link>

                                {/* Separator (placed before Outlet-rendered credential fields) */}
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"/>
                                    <span
                                        className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">or</span>
                                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"/>
                                </div>
                            </div>
                        )}

                        {/* ============= FORM WORKSPACE (<Outlet /> rendering sub-routes) ============= */}
                        <div className="min-h-fit">
                            <Outlet/>
                        </div>

                        {/* ============= FOOTER AREA (Navigation Links) ============= */}
                        {!isNextRoute && (
                            <div
                                className="pt-4 border-t border-slate-200 dark:border-slate-700 text-center text-sm text-slate-600 dark:text-slate-400 space-y-2">
                                {(isSsoRoute || isPasswordRoute) && (
                                    <>
                                        <p>
                                            Don't have an account?{' '}
                                            <Link to={`/auth/popup/register${location.search}`}
                                                  className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
                                                Sign up
                                            </Link>
                                        </p>
                                        <p>
                                            <Link to={`/auth/popup/forget${location.search}`}
                                                  className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
                                                Forgot password?
                                            </Link>
                                        </p>
                                    </>
                                )}
                                {isRegisterRoute && (
                                    <p>
                                        Already have an account?{' '}
                                        <Link to={`/auth/popup/password${location.search}`}
                                              className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
                                            Sign in
                                        </Link>
                                    </p>
                                )}
                                {isForgotRoute && (
                                    <p>
                                        <Link to={`/auth/popup/password${location.search}`}
                                              className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
                                            Back to login
                                        </Link>
                                    </p>
                                )}
                                {/* ✨ Clean separation: Keep footer clear when handling multi-channel verification tokens */}
                                {isVerifyRoute && (
                                    <p>
                                        <Link to="/auth/popup/register"
                                              className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
                                            ← Back to registration
                                        </Link>
                                    </p>
                                )}
                            </div>
                        )}
                            </>
                        )}
                    </div>
                </div>

                {/* ============= SCROLL INDICATOR (Mobile/Small Screens) ============= */}
                {showScrollIndicator && (
                    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 animate-bounce pointer-events-none">
                        <div className="text-center text-xs text-slate-500 dark:text-slate-400">
                            <div className="mb-1">Scroll down</div>
                            <div className="text-lg">↓</div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}